import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { Box } from '@ltvco/refresh-lib/theme';
import {
  ListYourCarWizard,
  ImagesStep,
  DetailsStep,
  PriceStep,
  ReviewStep,
  useListYourCar,
  useUserVehicles,
  useMarketplaceFacets,
  type ModerationResult,
} from '@ltvco/refresh-lib/v2';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { locationRequest } from 'api/requests/location/location';
import marketplaceLogoSrc from 'images/bumper_marketplace_logo_stacked.svg';

/** Ported from the Ember `list-your-car` service's fireGAEvent (eventCategory: 'List your car'). */
type ListYourCarTracker = (eventAction: string, eventLabel: string) => void;

/** Resolves a ZIP to city/state via the brand `/hk/dd/v2/location` service. */
async function lookupZip(
  zip: string,
): Promise<{ city: string; state: string } | null> {
  try {
    const data = await locationRequest.zipToLocation(zip);
    const location = data.entities.locations[0];
    return location ? { city: location.city, state: location.state } : null;
  } catch {
    return null;
  }
}

/**
 * Authenticated list-your-car route — the private-seller listing wizard.
 * Ported from the Ember `list-your-car` route; composes the lib step components
 * (photos → details → price → review) around the `useListYourCar` state
 * container. The optional `:inventoryId` param hydrates edit mode.
 */
export function MarketplaceListYourCar() {
  const navigate = useNavigate();
  const { inventoryId } = useParams();
  const returnToListings = () => navigate('/marketplace/listed-vehicles');
  const { trackEvent } = useContext(AppConfig);
  const trackListYourCar = useCallback<ListYourCarTracker>(
    (eventAction, eventLabel) =>
      trackEvent('List your car', eventAction, eventLabel),
    [trackEvent],
  );

  const { vehiclesQuery } = useUserVehicles();
  const initialVehicle = inventoryId
    ? (vehiclesQuery.data?.vehicles.find(
        (vehicle) => (vehicle.inventoryId ?? vehicle.id) === inventoryId,
      ) ?? null)
    : null;

  // Makes are loaded unscoped; models are facet-scoped to the selected make and
  // accumulated into a `modelsByMake` map as the user picks makes.
  const { makesList } = useMarketplaceFacets();
  const [modelsByMake, setModelsByMake] = useState<Record<string, string[]>>(
    {},
  );

  // Lifted above ReviewStep so it survives that step unmounting when the user
  // jumps to an earlier step via "Edit" and back.
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const {
    state,
    isEditMode,
    years,
    makes,
    models,
    colors,
    priceRange,
    daysOnMarket,
    setDetails,
    setPrice,
    setContact,
    setImages,
    removeExistingImage,
    moderateImage,
    moderateText,
    lookupVin,
    lookupZip: applyZip,
    submit,
    isSubmitting,
    submitError,
  } = useListYourCar({
    initialVehicle,
    makes: makesList,
    modelsByMake,
    lookupZip,
    onSubmitSuccess: () => {
      // isEditMode isn't destructured yet at this point in the same call;
      // inventoryId (its underlying source, set once from the route param) is
      // already available above.
      trackListYourCar(
        inventoryId ? 'Edit listing' : 'Post listing',
        'success',
      );
      returnToListings();
    },
  });

  // Prefills the VIN from the sell-your-car banner cross-sell
  // (`?vin=`) and triggers the same decode the details step's VIN field does
  // on blur, so the wizard opens with make/model/year/specs already filled.
  const [searchParams] = useSearchParams();
  const prefilledVinRef = useRef(false);
  useEffect(() => {
    const vinParam = searchParams.get('vin');
    if (!vinParam || prefilledVinRef.current || isEditMode) {
      return;
    }
    prefilledVinRef.current = true;
    setDetails({ vin: vinParam });
    lookupVin(vinParam);
  }, [searchParams, isEditMode, setDetails, lookupVin]);

  const wasSubmittingRef = useRef(isSubmitting);
  useEffect(() => {
    if (wasSubmittingRef.current && !isSubmitting && submitError) {
      trackListYourCar(isEditMode ? 'Edit listing' : 'Post listing', 'error');
    }
    wasSubmittingRef.current = isSubmitting;
  }, [isSubmitting, submitError, isEditMode, trackListYourCar]);

  const handleModerateImage = async (file: File): Promise<ModerationResult> => {
    const result = await moderateImage(file);
    trackListYourCar(
      'Moderation',
      result.isSafeContent
        ? 'image approved'
        : `image rejected ${result.failures.join(', ')}`,
    );
    return result;
  };

  const handleModerateText = async (
    text: string,
  ): Promise<ModerationResult> => {
    const result = await moderateText(text);
    trackListYourCar(
      'Moderation',
      result.isSafeContent
        ? 'text approved'
        : `text rejected ${result.failures.join(', ')}`,
    );
    return result;
  };

  const handleSubmit = (): void => {
    trackListYourCar(isEditMode ? 'Edit listing' : 'Post listing', 'click');
    submit();
  };

  const selectedMake = state.details.make ?? '';
  const { facets } = useMarketplaceFacets(
    selectedMake ? [selectedMake] : undefined,
  );

  useEffect(() => {
    if (!selectedMake || modelsByMake[selectedMake]) {
      return;
    }
    const models = (facets.model ?? []).map((facet) => facet.value);
    if (models.length > 0) {
      setModelsByMake((prev) => ({ ...prev, [selectedMake]: models }));
    }
  }, [selectedMake, facets, modelsByMake]);

  return (
    <Box maxWidth={700} mx="auto" px={2} pt={3} pb={10}>
      <ListYourCarWizard
        isEditMode={isEditMode}
        onExit={returnToListings}
        steps={[
          {
            title: 'Add photos',
            render: ({ goNext }) => {
              const onNext = (): void => {
                trackListYourCar('Next section', 'photos');
                goNext();
              };
              return (
                <ImagesStep
                  images={state.images}
                  onImagesChange={setImages}
                  onModerateImage={handleModerateImage}
                  onRemoveExistingImage={removeExistingImage}
                  onNext={onNext}
                />
              );
            },
          },
          {
            title: 'Details',
            render: ({ goBack, goNext }) => {
              const onBack = (): void => {
                trackListYourCar('Back section', 'details');
                goBack();
              };
              const onNext = (): void => {
                trackListYourCar('Next section', 'details');
                goNext();
              };
              return (
                <DetailsStep
                  details={state.details}
                  onDetailsChange={setDetails}
                  location={state.sellerLocation}
                  years={years}
                  makes={makes}
                  models={models}
                  colors={colors}
                  onLookupVin={lookupVin}
                  onLookupZip={applyZip}
                  onModerateText={handleModerateText}
                  onBack={onBack}
                  onNext={onNext}
                  isEditMode={isEditMode}
                />
              );
            },
          },
          {
            title: 'Price',
            render: ({ goBack, goNext }) => {
              const onBack = (): void => {
                trackListYourCar('Back section', 'price');
                goBack();
              };
              const onNext = (): void => {
                trackListYourCar('Next section', 'price');
                goNext();
              };
              return (
                <PriceStep
                  price={state.price}
                  onPriceChange={setPrice}
                  priceRange={priceRange}
                  marketplaceLogoSrc={marketplaceLogoSrc}
                  onBack={onBack}
                  onNext={onNext}
                />
              );
            },
          },
          {
            title: 'Review',
            render: ({ goBack, goToStep }) => {
              const onEditSection = (section: string): void => {
                const stepBySection: Record<string, number> = {
                  images: 0,
                  details: 1,
                  price: 2,
                };
                goToStep(stepBySection[section] ?? 0);
              };
              const onBack = (): void => {
                trackListYourCar('Back section', 'review');
                goBack();
              };
              return (
                <ReviewStep
                  details={state.details}
                  price={state.price}
                  specs={state.specs}
                  location={state.sellerLocation}
                  images={state.images}
                  contact={state.contactInfo}
                  onContactChange={setContact}
                  agreedToTerms={agreedToTerms}
                  onAgreedToTermsChange={setAgreedToTerms}
                  onEditSection={onEditSection}
                  onBack={onBack}
                  onSubmit={handleSubmit}
                  isEditMode={isEditMode}
                  isSubmitting={isSubmitting}
                  submitError={submitError}
                  daysOnMarket={daysOnMarket}
                />
              );
            },
          },
        ]}
      />
    </Box>
  );
}
